import React, { useState } from 'react';
import axios from 'axios';
import { Download, Loader, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/public/respaldo/descargar/`;

/**
 * Genera un respaldo completo de la base y lo baja al equipo de quien lo pide.
 *
 * No reemplaza al respaldo automático, que corre cada hora sin que nadie se
 * acuerde. Lo que aporta es una copia que no vive en ninguna cuenta de ningún
 * proveedor: si la cuenta del servidor se suspendiera, esta sigue estando.
 *
 * La descarga no puede ser un enlace común porque el endpoint exige la cabecera
 * de autenticación. Se pide el archivo, se lo convierte en una dirección
 * temporal del navegador y se la activa.
 */
export default function DescargarRespaldo() {
    const [generando, setGenerando] = useState(false);
    const [listo, setListo] = useState(null);
    const [error, setError] = useState(null);

    const descargar = async () => {
        setGenerando(true);
        setError(null);
        setListo(null);
        try {
            const respuesta = await axios.get(API, { responseType: 'blob' });

            const nombre = (respuesta.headers['content-disposition'] || '')
                .match(/filename="?([^"]+)"?/)?.[1] || 'respaldo.sql.gz';

            const url = window.URL.createObjectURL(respuesta.data);
            const enlace = document.createElement('a');
            enlace.href = url;
            enlace.download = nombre;
            document.body.appendChild(enlace);
            enlace.click();
            enlace.remove();

            // La dirección temporal se libera más tarde, no acá. `click()` solo
            // encola la descarga: el navegador lee el contenido después, y
            // revocar en la línea siguiente le saca el archivo de las manos
            // antes de que alcance a leerlo. El resultado es una descarga que
            // aparece en la carpeta pero sale vacía.
            setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

            const kb = Math.max(1, Math.round(respuesta.data.size / 1024));
            setListo({ nombre, kb, tablas: respuesta.headers['x-tablas'] });
        } catch (e) {
            // El error viaja como blob porque se pidió el archivo en ese formato:
            // hay que leerlo para poder mostrar el motivo real.
            let detalle = 'No se pudo generar el respaldo.';
            try {
                const texto = await e.response?.data?.text?.();
                if (texto) detalle = JSON.parse(texto).error || detalle;
            } catch { /* se queda el mensaje genérico */ }
            if (e.response?.status === 429) {
                detalle = 'Demasiadas descargas seguidas. Esperá unos minutos.';
            }
            setError(detalle);
        } finally {
            setGenerando(false);
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
                Descarga una copia completa de la base de datos, con todos los negocios,
                para guardarla donde vos quieras. El servidor ya hace copias automáticas
                cada hora; esta sirve para tener una <strong>fuera de todo proveedor</strong>,
                que siga estando aunque se pierda el acceso a la cuenta del servidor.
            </p>

            {error && (
                <div className="aviso-error">
                    {error}
                </div>
            )}

            {listo && (
                <div className="bg-exito-50 border border-exito-200 rounded-2xl px-4 py-3 flex items-start gap-2">
                    <CheckCircle size={16} className="text-exito-500 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <p className="font-black text-exito-800">{listo.nombre}</p>
                        <p className="text-exito-700 text-xs">
                            {listo.kb.toLocaleString('es-PY')} KB
                            {listo.tablas ? ` · ${listo.tablas} tablas` : ''} · revisá tu carpeta de descargas
                        </p>
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={descargar}
                disabled={generando}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-negro-950 px-5 py-3 rounded-2xl font-black text-sm transition-colors"
            >
                {generando
                    ? <><Loader size={16} className="animate-spin" /> Generando el respaldo...</>
                    : <><Download size={16} /> Descargar respaldo completo</>}
            </button>

            {generando && (
                <p className="text-xs text-slate-400">
                    El archivo se arma y se verifica antes de enviarse, así que puede
                    tardar. No cierres la pestaña.
                </p>
            )}

            <p className="text-[11px] text-slate-400 leading-relaxed border-t border-slate-100 pt-3">
                El archivo contiene los datos de todos los negocios. Guardalo en un lugar
                seguro: quien lo tenga puede leer ventas, clientes y precios de cada
                ferretería. Cada descarga queda registrada.
            </p>
        </div>
    );
}
